import React from 'react'
import { Helmet } from 'react-helmet'
import Hero from '../components/nav/Hero'
import { FiUsers, FiBookOpen, FiCamera, FiCheck, FiZap, FiLock } from "react-icons/fi";
import { Link } from 'react-router-dom';
import { FiShield, FiCheckCircle, FiArrowRight, FiSettings } from "react-icons/fi";
import FadeInSection from '../components/nav/FadeInSection';
import manning from '../assets/manning.jpeg'
import corporate from '../assets/corporate.png'
import training from '../assets/training.jpeg'
import devices from '../assets/devices.png'
import electrical from '../assets/electrical.png'
import burglar from '../assets/burglar.png'

function ServicesCTA() {
    return (
        <section data-theme="guardhartDark" className="relative py-24 bg-base-200 border-b border-zinc-800 overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

                    <div className="text-center lg:text-left">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-base-content mb-6">
                            Need a tailored security solution?
                        </h2>
                        <p className="text-base text-zinc-400 mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                            Whether you need a single guard, a full CCTV rollout, or executive protection, our team will design a comprehensive plan that fits your exact operational needs and budget.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 max-w-lg mx-auto lg:mx-0">
                            <div className="flex items-center gap-3 text-left">
                                <FiCheckCircle className="w-5 h-5 text-primary shrink-0" />
                                <span className="text-sm text-zinc-300">Free site assessment</span>
                            </div>
                            <div className="flex items-center gap-3 text-left">
                                <FiCheckCircle className="w-5 h-5 text-primary shrink-0" />
                                <span className="text-sm text-zinc-300">Transparent pricing</span>
                            </div>
                            <div className="flex items-center gap-3 text-left">
                                <FiCheckCircle className="w-5 h-5 text-primary shrink-0" />
                                <span className="text-sm text-zinc-300">Rapid deployment</span>
                            </div>
                            <div className="flex items-center gap-3 text-left">
                                <FiCheckCircle className="w-5 h-5 text-primary shrink-0" />
                                <span className="text-sm text-zinc-300">24/7 dedicated support</span>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                            <Link
                                to="/contact"
                                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-content px-8 py-4 text-xs font-bold uppercase tracking-widest rounded active:scale-95 transition-all hover:brightness-110"
                            >
                                Get a Custom Quote
                                <FiArrowRight className="w-4 h-4" />
                            </Link>
                            <a
                                href="tel:+254714843383"
                                className="inline-flex items-center justify-center gap-2 border border-zinc-700 text-zinc-100 px-8 py-4 text-xs font-bold uppercase tracking-widest rounded active:scale-95 transition-all hover:bg-zinc-800 hover:border-zinc-600"
                            >
                                Call Us Now
                            </a>
                        </div>
                    </div>

                    <div className="hidden lg:flex justify-center">
                        <div className="relative">
                            <div className="absolute inset-0 bg-primary/5 rounded-full blur-3xl" />
                            <div className="relative size-64 rounded-full border-[16px] border-zinc-200 flex items-center justify-center">
                                <FiSettings className="text-[100px] text-primary/20" />
                            </div>
                            <div className="absolute -top-4 -right-4 bg-base-100 border border-zinc-200 rounded-lg px-4 py-2 shadow-lg">
                                <span className="text-xs font-bold text-primary">Free Audit</span>
                            </div>
                            <div className="absolute -bottom-4 -left-4 bg-base-100 border border-zinc-200 rounded-lg px-4 py-2 shadow-lg">
                                <span className="text-xs font-bold text-primary">6 Service Lines</span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}


const ServiceDetail = ({ title, description, features, icon: Icon, imageSrc, isReversed }) => {
    return (
        <section className="relative border-b border-zinc-200 group bg-base-100">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2">

                {/* Content Side */}
                <div className={`p-8 md:p-16 flex flex-col justify-center ${isReversed ? 'lg:order-2' : ''}`}>
                    <div className="mb-6 text-primary">
                        <Icon className="w-10 h-10" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-base-content mb-6">
                        {title}
                    </h2>
                    <p className="text-zinc-600 text-lg leading-relaxed mb-8">
                        {description}
                    </p>
                    <ul className="space-y-4">
                        {features.map((feature, index) => (
                            <li key={index} className="flex items-center gap-3 text-zinc-700">
                                <FiCheck className="w-5 h-5 text-primary shrink-0" />
                                <span>{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Image Side */}
                <div className={`h-[400px] lg:h-auto overflow-hidden border-b lg:border-b-0 ${isReversed ? 'lg:border-r lg:order-1' : 'lg:border-l'} border-zinc-200 relative`}>
                    <div
                        className="w-full h-full bg-cover bg-center transition-all duration-700 grayscale group-hover:grayscale-0 group-hover:scale-105"
                        style={{ backgroundImage: `url(${imageSrc})` }}
                    />
                </div>

            </div>
        </section>
    );
};
function ServicesDetails() {
    const services = [
        {
            title: "Close Protection",
            description: "Discreet, high-tier personal security for executives and high-net-worth individuals. Our operators are trained in advanced threat assessment, secure logistics, and emergency response to ensure absolute peace of mind.",
            features: ["Executive & VIP Protection", "Residential Security Teams (RST)"],
            icon: FiShield,
            imageSrc: manning,
            isReversed: false
        },
        {
            title: "Manning Services",
            description: "Precision-trained personnel for static and mobile security assignments. From front-of-house corporate concierge roles to high-security perimeter patrol, our teams integrate seamlessly with your organization's culture.",
            features: ["Corporate Reception & Concierge", "Asset Protection & Perimeter Guarding", "24/7 Rosters & Patrols"],
            icon: FiUsers,
            imageSrc: corporate,
            isReversed: true
        },
        {
            title: "Electrical Security",
            description: "Comprehensive electrical security installations including electric fencing, access control gates, and perimeter electrification. We design and deploy robust barriers that deter intruders and integrate with your existing surveillance systems.",
            features: ["Electric Fence Installation & Maintenance", "Automated Gate Systems", "Perimeter Electrification"],
            icon: FiZap,  // You'll need to import this
            imageSrc: electrical,  // Add your image import
            isReversed: false
        },
        {
            title: "Burglar Proofing",
            description: "Physical hardening of residential and commercial properties with premium security doors, window bars, and reinforced entry points. Our solutions combine aesthetics with impenetrable defense to protect what matters most.",
            features: ["Security Doors & Frames", "Window Bars & Grilles", "Reinforced Lock Systems"],
            icon: FiLock,  // You'll need to import this
            imageSrc: burglar,  // Add your image import
            isReversed: true
        },
        {
            title: "Training & Consulting",
            description: "Strategic risk management and professional certification programs. We empower your internal teams with tactical knowledge, from emergency response drills to complex corporate risk assessments and vulnerability analysis.",
            features: ["Risk & Vulnerability Assessment", "Crisis Management Training", "In-house Guard Training Programs"],
            icon: FiBookOpen,
            imageSrc: training,
            isReversed: true
        },
        {
            title: "Security Systems",
            description: "Integration of advanced surveillance, access control, and threat detection hardware. We design bespoke technical solutions that provide real-time situational awareness and automated defense for critical infrastructure.",
            features: ["AI-Powered Surveillance (CCTV)", "Alarm Back Up & Response", "Visitor Management Systems"],
            icon: FiCamera,
            imageSrc: devices,
            isReversed: false
        }
    ];

    return (
        <div className="w-full">
            {services.map((service, index) => (
                <FadeInSection key={index}>
                    <ServiceDetail {...service} />
                </FadeInSection>
            ))}
        </div>
    );
}


function ServicesPage() {
    const url = window.location.href
    return (
        <div>
            <Helmet>
                <title>Security Services | Manning, Close Protection, CCTV & Training - Guardhart Kenya</title>
                <link rel="canonical" href={url} />
                <meta name="description" content="Explore Guardhart Security Services - professional manning services, executive close protection, AI-powered CCTV surveillance, alarm response, and security training & consulting across Kenya." />

                <meta property="og:type" content="website" />
                <meta property="og:url" content={url} />
                <meta property="og:title" content="Security Services | Manning, Close Protection, CCTV & Training - Guardhart Kenya" />
                <meta property="og:description" content="Professional manning services, executive close protection, AI-powered CCTV surveillance, alarm response, and security training & consulting across Kenya." />

                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:title" content="Security Services | Manning, Close Protection, CCTV & Training - Guardhart Kenya" />
                <meta property="twitter:description" content="Professional manning services, executive close protection, AI-powered CCTV surveillance, alarm response, and security training & consulting across Kenya." />
            </Helmet>
            <Hero
                label="What We Do"
                title="Complete Security Solutions."
                description="From armed guards and executive protection to smart CCTV and alarm systems. Six service lines, one trusted partner for your home, business, or estate."
                primaryBtnText="Get a Quote"
                primaryBtnLink="/contact"
                secondaryBadgeText="6 Service Lines"
            />
            <ServicesDetails />
            <FadeInSection>
                <ServicesCTA />
            </FadeInSection>
        </div>
    )
}

export default ServicesPage